/**
 * Runtime state for the editable shared architecture map workspace
 * (b2c / b2b / mvp stages). Single source of truth for nodes + edges across
 * all three stages; per-stage highlights live in `map_stage_highlights`
 * (managed by useArchitectureMapPage, not here).
 *
 * Mirrors useB2bUsersStage (which handles the b2b-users stage's own workspace).
 */

import { useCallback, useMemo } from "react";

import { useArchitectureWorkspace } from "@/application/use-cases/architecture/use-architecture-workspace.queries";
import {
  mapEdgesToJson,
  mapNodesToJson,
  parseMapEdges,
  parseMapNodes,
} from "@/infrastructure/repositories/architecture-workspace.mappers";
import {
  type ArchitectureMapEdge,
  type ArchitectureMapNode,
  MAP_SEED_EDGES,
  MAP_SEED_NODES,
} from "@/presentation/data/architecture-map-data";

export interface UseMapWorkspace {
  nodes: ArchitectureMapNode[];
  edges: ArchitectureMapEdge[];
  isLoading: boolean;
  setNodes: (next: readonly ArchitectureMapNode[]) => void;
  setEdges: (next: readonly ArchitectureMapEdge[]) => void;
  resetToSeed: () => void;
}

export function useMapWorkspace(): UseMapWorkspace {
  const { workspace, isLoading, patch } = useArchitectureWorkspace();

  const rawNodes = useMemo(
    () => (workspace ? parseMapNodes(workspace.map_nodes) : []),
    [workspace]
  );
  const rawEdges = useMemo(
    () => (workspace ? parseMapEdges(workspace.map_edges) : []),
    [workspace]
  );

  // First-visit seed: when the workspace JSONB is empty, hydrate the editor
  // with the seed graph in-memory. The first user mutation triggers
  // persistence with the seed already present in component state.
  //
  // Nodes and edges fall back independently: previously edges only seeded
  // when BOTH arrays were empty, which broke for workspaces that had
  // map_nodes populated but map_edges still '[]' (e.g. after migration 0032
  // restored positions without seeding edges — see migration 0033 for the
  // data-side fix). Trade-off: a user who deletes every edge will see them
  // reappear on refresh; the "Reset map" path handles full clears
  // explicitly, so this is acceptable.
  //
  // CRITICAL: memoize so identity is stable across renders.
  const nodes = useMemo(
    () => (rawNodes.length === 0 ? MAP_SEED_NODES.slice() : rawNodes),
    [rawNodes]
  );
  const edges = useMemo(
    () => (rawEdges.length === 0 ? MAP_SEED_EDGES.slice() : rawEdges),
    [rawEdges]
  );

  const setNodes = useCallback(
    (next: readonly ArchitectureMapNode[]) => {
      patch({ map_nodes: mapNodesToJson(next) });
    },
    [patch]
  );

  const setEdges = useCallback(
    (next: readonly ArchitectureMapEdge[]) => {
      patch({ map_edges: mapEdgesToJson(next) });
    },
    [patch]
  );

  const resetToSeed = useCallback(() => {
    patch({
      map_nodes: mapNodesToJson(MAP_SEED_NODES),
      map_edges: mapEdgesToJson(MAP_SEED_EDGES),
    });
  }, [patch]);

  return { nodes, edges, isLoading, setNodes, setEdges, resetToSeed };
}
